
import React from 'react';
import { useTranslation } from 'react-i18next';
import { 
  Table, TableBody, TableCell, 
  TableHead, TableHeader, TableRow 
} from "@/components/ui/table";
import Button from '@/components/ui-elements/button';
import { Card, CardContent } from "@/components/ui/card";
import { Database } from '@/integrations/supabase/types';
import { Edit, Trash2 } from 'lucide-react';

type Reward = Database['public']['Tables']['rewards']['Row'];

interface RewardsTableProps {
  rewards: Reward[];
  loading: boolean;
  handleEdit: (reward: Reward) => void;
  handleToggleActive: (id: string, currentState: boolean) => Promise<void>;
  handleDelete: (id: string) => Promise<void>;
}

const RewardsTable: React.FC<RewardsTableProps> = ({
  rewards,
  loading,
  handleEdit,
  handleToggleActive,
  handleDelete
}) => {
  const { t } = useTranslation();

  return (
    <Card>
      <CardContent className="pt-6">
        <div className="border rounded-md overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{t('admin.rewardName')}</TableHead>
                <TableHead>{t('admin.points')}</TableHead>
                <TableHead>{t('admin.tier')}</TableHead>
                <TableHead>{t('admin.quantity')}</TableHead>
                <TableHead>{t('admin.status')}</TableHead>
                <TableHead>{t('admin.actions')}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={6} className="text-center py-4">
                    {t('admin.loading')}
                  </TableCell>
                </TableRow>
              ) : rewards.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} className="text-center py-4">
                    {t('admin.noRewardsFound')}
                  </TableCell>
                </TableRow>
              ) : (
                rewards.map((reward) => (
                  <TableRow key={reward.id}>
                    <TableCell className="font-medium">{reward.name}</TableCell>
                    <TableCell>{reward.points_required}</TableCell>
                    <TableCell>{reward.tier_required || 'Standard'}</TableCell>
                    <TableCell>{reward.quantity_available || t('admin.unlimited')}</TableCell>
                    <TableCell>
                      <span className={`px-2 py-1 rounded-full text-xs ${
                        reward.is_active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                      }`}>
                        {reward.is_active ? t('admin.active') : t('admin.inactive')}
                      </span>
                    </TableCell>
                    <TableCell>
                      <div className="flex space-x-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleEdit(reward)}
                          leftIcon={<Edit className="h-3 w-3" />}
                        >
                          {t('admin.edit')}
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleToggleActive(reward.id, reward.is_active)}
                        >
                          {reward.is_active ? t('admin.deactivate') : t('admin.activate')}
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          className="text-red-500 hover:text-red-700"
                          onClick={() => handleDelete(reward.id)}
                          leftIcon={<Trash2 className="h-3 w-3" />}
                        >
                          {t('admin.delete')}
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
      </CardContent>
    </Card>
  );
};

export default RewardsTable;
